<template>
  <div class="form-group">
    <label v-if="label"
           v-bind:class="{required: hasRequired}">
      {{label}}
    </label>
    <div class="input-group d-none d-lg-flex">
      <select class="form-control"
              @change="changeMonth"
              title="Month"
              :disabled="disabled"
              v-bind:class="{'is-invalid': validation.$error}">
        <option v-for="mon in months"
                v-bind:key="mon.id"
                :value="mon"
                :selected="mon === selectedMonth"
                :disabled="readonly">
          {{mon}}
        </option>
      </select>

      <input v-model="selectedDay"
             class="form-control"
             title="Day"
             @input="changeDay"
             type="number"
             min="1"
             max="31"
             :disabled="disabled"
             :readonly="readonly"
             v-bind:class="{'is-invalid': validation.$error}"
             @keypress="isNumber" />
      <input v-model="selectedYear"
             class="form-control"
             title="Year"
             :readonly="readonly"
             @input="changeYear"
             :disabled="disabled"
             type="number"
             v-bind:class="{'is-invalid': validation.$error}"
             @keypress="isNumber" />
      <div class="input-group-append">
        <button class="btn btn-outline-secondary"
                type="button"
                :disabled="readonly || disabled"
                @click="openDatePicker">
          <font-awesome-icon :icon="['far', 'calendar-alt']" />
        </button>
      </div>
    </div>
    <date-picker
      ref="myDatePicker"
      input-class="d-none"
      v-bind:value="value"
      @input="changeDateFields">
    </date-picker>
    <input type="date"
           v-model="selectedDate"
           :readonly="readonly"
           class="form-control d-lg-none"
           :disabled="disabled"
           @input="changeNativeDate"
           v-bind:class="{ 'is-invalid': validation.$error }"
           title="Date Input"/>
    <div class="invalid-feedback" v-if="errorMessage">
      {{errorMessage}}
    </div>
  </div>
</template>
<script>
import { ValidationMixin } from '@/js/Mixins';
import moment from 'moment';
import DatePicker from 'vuejs-datepicker';
import BaseSelect from './BaseSelect';

export default {
  components: { DatePicker, BaseSelect },
  name: 'base-date-picker',
  mixins: [ValidationMixin],
  props: {
    value: Date,
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.id = this._uid;

    if (this.value) {
      this.setDateFields(this.value);
    } else {
      this.selectedYear = null;
      this.selectedMonth = 'January';
      this.selectedDay = null;
    }

    if (this.value) {
      const m = moment(this.value);
      if (m.isValid()) {
        m.startOf('day');
        this.$emit('input', m.toDate());
      }
    }
  },
  data() {
    return {
      id: null,
      selectedMonth: null,
      selectedDay: null,
      selectedYear: null,
      selectedDate: null,
      months: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
    };
  },
  methods: {
    isNumber(evt) {
      const event = evt || window.event;
      const charCode = (event.which) ? event.which : event.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        evt.preventDefault();
      } else {
        return true;
      }
      return false;
    },
    openDatePicker() {
      this.$refs.myDatePicker.showCalendar();
    },
    changeDateFields(event) {
      this.validation.$touch();
      this.setDateFields(event);
      this.$emit('input', event);
    },
    changeDay(event) {
      const myDate = moment(`${this.selectedYear}-${this.selectedMonth}-${event.target.value}`, 'YYYY-MMMM-D', true);
      const payload = myDate.isValid() ? myDate.toDate() : null;
      this.setDateFields(payload);
      this.validation.$touch();
      this.$emit('input', payload);
    },
    changeYear(event) {
      const myDate = moment(`${event.target.value}-${this.selectedMonth}-${this.selectedDay}`, 'YYYY-MMMM-D', true);
      const payload = myDate.isValid() ? myDate.toDate() : null;
      this.setDateFields(payload);
      this.validation.$touch();
      this.$emit('input', payload);
    },
    changeMonth(event) {
      const myDate = moment(`${this.selectedYear}-${event.target.value}-${this.selectedDay}`, 'YYYY-MMMM-D', true);
      const payload = myDate.isValid() ? myDate.toDate() : null;
      this.setDateFields(payload);

      this.validation.$touch();
      this.$emit('input', payload);
    },
    changeNativeDate(event) {
      const myDate = moment(event.target.value, 'YYYY-MM-DD');
      if (!myDate.isValid() || myDate.year() < 1000) {
        return;
      }
      const payload = myDate.toDate();
      this.setDateFields(payload);
      this.validation.$touch();
      this.$emit('input', payload);
    },
    setDateFields(newDate) {
      if (newDate instanceof Date == false && newDate != null) {
        newDate = new Date(newDate);
      }
      if (newDate) {
        this.selectedYear = newDate.getFullYear();
        this.selectedDay = newDate.getDate();
        // internet exploder randomly puts stupid ass characters in the string, ruining any future string comparisons
        // that's beyond stupid
        this.selectedMonth = newDate.toLocaleString('en-us', { month: 'long' }).replace(/\u200E/g, '');

        const month2Digit = newDate.toLocaleString('en-us', { month: '2-digit' });
        const day2Digit = newDate.toLocaleString('en-us', { day: '2-digit' });
        this.selectedDate = `${this.selectedYear}-${month2Digit}-${day2Digit}`;
      }
    },
  },
  computed: {
    mydate() {
      return moment(`${this.selectedYear}-${this.selectedMonth}-${this.selectedDay}`, 'YYYY-MMMM-D').toDate();
    },
  },
  watch: {
    // this is a hack since sometimes the prop isn't set before render somehow or something.
    value() {
      this.setDateFields(this.value);
    },
  },
};
</script>

